/**
 * "Taslaklarım" (full post drafts) card.
 *
 * Templates (post_editor_templates) sadece DÜZENİ saklar — font/pos/color.
 * Drafts (post_editor_drafts) ise ÜRETİLEN tam post'un **payload**'ını
 * saklar: seçili bg_id, photo_id, overlay, metin katmanları (metin dahil),
 * output_format, caption, hashtags.
 *
 * İş akışı:
 *   1. Admin bir post üretir.
 *   2. "Taslak kaydet" → mevcut compose payload saklanır.
 *   3. Daha sonra bu karttan:
 *        • "Yükle"       → payload editöre yüklenir, admin düzenleyebilir.
 *        • "Hemen Üret"  → payload direkt /assets'e POST edilir, aynı
 *                          post'un yeni kopyası kütüphaneye düşer.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Zap, FileEdit, Trash2, Save, Loader2 } from "lucide-react";
import { api, errMsg } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";

export function DraftsCard({ platformId, platformLabel, categoryId, buildPayload, onLoad, onProduced }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [savingBusy, setSavingBusy] = useState(false);
  const [producingId, setProducingId] = useState("");

  const { data: drafts = [] } = useQuery({
    queryKey: ["pe", "drafts", platformId, categoryId],
    queryFn: () => api.get("/admin/post-editor/drafts", {
      params: { category_id: categoryId || "", platform: platformId },
    }).then((r) => r.data),
    enabled: !!platformId,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["pe", "drafts", platformId, categoryId] });

  const save = async () => {
    if (!categoryId) { toast.error("Önce bir kategori seç"); return; }
    const payload = buildPayload();
    if (!payload) { toast.error("Taslak için önce içeriği hazırla"); return; }
    setSavingBusy(true);
    try {
      await api.post("/admin/post-editor/drafts", {
        category_id: categoryId,
        platform: platformId,
        name: (name || "").trim() || "Taslak",
        payload,
      });
      setName("");
      invalidate();
      toast.success("Taslak kaydedildi");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSavingBusy(false);
  };

  const produceNow = async (draft) => {
    setProducingId(draft.id);
    try {
      // Payload birebir aynı → sunucuda `/assets` yeni bir dosya üretir.
      const { data } = await api.post("/admin/post-editor/assets", draft.payload || {});
      qc.invalidateQueries({ queryKey: ["pe", "assets"] });
      onProduced?.(data);
      toast.success("Taslaktan yeni post üretildi");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setProducingId("");
  };

  const remove = async (draft) => {
    if (!window.confirm(`"${draft.name}" taslağını sil?`)) return;
    try {
      await api.delete(`/admin/post-editor/drafts/${draft.id}`);
      invalidate();
      toast.success("Taslak silindi");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <section className="bg-white rounded-xl border border-amber-200 shadow-sm p-3" data-testid="pe-drafts-card">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-amber-900 flex-1 leading-tight">
          Taslaklarım · {platformLabel}
        </span>
        <span className="text-[10px] text-stone-400 shrink-0">{drafts.length}</span>
      </div>

      <div className="flex gap-1 mb-2">
        <Input value={name}
               onChange={(e) => setName(e.target.value)}
               placeholder="Taslak adı (opsiyonel)"
               className="h-8 text-xs"
               data-testid="pe-drafts-name" />
        <Button size="sm" onClick={save} disabled={savingBusy || !categoryId}
                className="bg-amber-600 hover:bg-amber-700 h-8 px-2"
                data-testid="pe-drafts-save">
          {savingBusy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
        </Button>
      </div>
      <p className="text-[10px] text-stone-500 mb-2 leading-tight">
        Mevcut düzen + metinler + fotoğraf tek bir "taslak"ta saklanır. "Hemen Üret" ile birebir aynı post'un yeni kopyası otomatik üretilir.
      </p>

      {drafts.length === 0 ? (
        <p className="text-[11px] text-stone-400 italic text-center py-3">Henüz taslak yok.</p>
      ) : (
        <ul className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1" data-testid="pe-drafts-list">
          {drafts.map((d, i) => (
            <li key={d.id} className="rounded border border-amber-100 bg-amber-50/50 p-2"
                data-testid={`pe-drafts-item-${i}`}>
              <p className="text-[11px] font-semibold text-slate-900 truncate" title={d.name}>
                {d.name}
              </p>
              <p className="text-[9px] text-stone-500 line-clamp-1">
                {(d.payload?.texts || []).map((t) => t.text).filter(Boolean).join(" · ") || <em>(metin yok)</em>}
              </p>
              <div className="flex items-center gap-1 mt-1.5">
                <Button size="sm" variant="outline"
                        onClick={() => { onLoad?.(d.payload || {}); toast.success("Taslak editöre yüklendi"); }}
                        className="h-6 px-2 text-[10px] flex-1"
                        data-testid={`pe-drafts-load-${i}`}
                        title="Editöre yükle, düzenle, sonra üret">
                  <FileEdit size={10} className="mr-1" /> Yükle
                </Button>
                <Button size="sm"
                        onClick={() => produceNow(d)}
                        disabled={producingId === d.id}
                        className="bg-amber-600 hover:bg-amber-700 h-6 px-2 text-[10px] flex-1"
                        data-testid={`pe-drafts-produce-${i}`}
                        title="Yeni kopya üret (düzenleme yok)">
                  {producingId === d.id
                    ? <Loader2 size={10} className="animate-spin" />
                    : <><Zap size={10} className="mr-1" /> Hemen Üret</>}
                </Button>
                <button type="button" onClick={() => remove(d)}
                        className="p-1 rounded hover:bg-red-50 text-red-600"
                        data-testid={`pe-drafts-del-${i}`}
                        title="Sil">
                  <Trash2 size={11} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
